const initializeProduct = (root) => {
  if (!root || root.dataset.productInitialized === 'true') return;
  root.dataset.productInitialized = 'true';
    const viewedProductHandle = root.dataset.productHandle;
    if (viewedProductHandle) {
      try {
        const storageKey = 'ptc-recently-viewed';
        const storedHandles = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        const recentHandles = [viewedProductHandle, ...storedHandles.filter((handle) => handle !== viewedProductHandle)].slice(0, 12);
        window.localStorage.setItem(storageKey, JSON.stringify(recentHandles));
      } catch (error) {
        // Browsing remains functional when storage is disabled or unavailable.
      }
    }

    const form = root.querySelector('[data-product-form]');
    const select = root.querySelector('[data-variant-select]');
    const add = root.querySelector('[data-add-to-cart]');
    const stickyAdd = root.querySelector('[data-sticky-add]');
    const price = root.querySelector('[data-product-price]');
    const finalPrice = root.querySelector('[data-final-price]');
    const comparePriceElement = root.querySelector('[data-compare-price]');
    const saleBadge = root.querySelector('[data-sale-badge]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const sku = root.querySelector('[data-product-sku]');
    const quantity = root.querySelector('[data-quantity-input]');
    const stock = root.querySelector('.ptc-product-form__stock');
    const currency = root.dataset.currency || document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'MXN';
    const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'es-MX', { style: 'currency', currency }).format(cents / 100);


    // Back in Stock apps are rendered asynchronously by Shopify/app scripts.
    // Keep the explanatory note outside the app-owned DOM and only reveal it
    // when that app has actually rendered a visible notification control.
    const backInStockNotes = [...root.querySelectorAll('[data-back-in-stock-note]')];
    const visibleElement = (element) => {
      if (!(element instanceof HTMLElement)) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      const rect = element.getBoundingClientRect();
      return rect.width > 1 && rect.height > 1;
    };
    const backInStockPattern = /(notificarme|notif[ií]came|av[ií]same|back\s*in\s*stock|notify\s*me|cuando\s+est[eé]\s+disponible)/i;
    const syncBackInStockNotes = () => {
      backInStockNotes.forEach((note) => {
        const app = note.nextElementSibling?.matches?.('[data-back-in-stock-app]')
          ? note.nextElementSibling
          : null;
        if (!app) {
          note.hidden = true;
          return;
        }

        const interactive = [
          ...app.querySelectorAll('button, a, input[type="button"], input[type="submit"], [role="button"]')
        ];
        const hasVisibleNotifyControl = interactive.some((element) => {
          if (!visibleElement(element)) return false;
          const label = [
            element.textContent,
            element.getAttribute('value'),
            element.getAttribute('aria-label'),
            element.getAttribute('title')
          ].filter(Boolean).join(' ');
          return backInStockPattern.test(label);
        });

        const visibleAppChildren = [...app.children].some((element) => visibleElement(element));
        const visibleAppText = visibleElement(app) && backInStockPattern.test(app.innerText || '');
        note.hidden = !(hasVisibleNotifyControl || visibleAppText || visibleAppChildren && backInStockPattern.test(app.textContent || ''));
      });
    };

    if (backInStockNotes.length) {
      let backInStockFrame = 0;
      const scheduleBackInStockSync = () => {
        window.cancelAnimationFrame(backInStockFrame);
        backInStockFrame = window.requestAnimationFrame(syncBackInStockNotes);
      };
      const backInStockObserver = new MutationObserver(scheduleBackInStockSync);
      backInStockObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled']
      });
      syncBackInStockNotes();
      [250, 750, 1500, 3000].forEach((delay) => window.setTimeout(syncBackInStockNotes, delay));
    }

    select?.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      const available = !option.disabled;
      const variantPrice = Number(option.dataset.price || 0);
      const comparePrice = Number(option.dataset.comparePrice || 0);
      const hasDiscount = comparePrice > variantPrice;
      const formatted = option.dataset.priceFormatted || money(variantPrice);
      const compareFormatted = option.dataset.comparePriceFormatted || money(comparePrice);
      const discountPercent = hasDiscount ? Math.round(((comparePrice - variantPrice) * 100) / comparePrice) : 0;

      if (price) price.classList.toggle('is-sale', hasDiscount);
      if (finalPrice) finalPrice.textContent = formatted;
      if (comparePriceElement) {
        comparePriceElement.hidden = !hasDiscount;
        comparePriceElement.textContent = hasDiscount ? compareFormatted : '';
      }
      if (saleBadge) {
        saleBadge.hidden = !hasDiscount;
        saleBadge.textContent = hasDiscount ? `-${discountPercent}%` : '';
      }
      if (stickyPrice) stickyPrice.textContent = formatted;
      if (sku) sku.textContent = option.dataset.sku ? `SKU: ${option.dataset.sku}` : '';
      if (stock) stock.innerHTML = `<i aria-hidden="true"></i>${available ? 'Disponible' : 'Agotado'}`;
      [add, stickyAdd].forEach((button) => {
        if (!button) return;
        button.disabled = !available;
        button.textContent = available ? (button === stickyAdd ? 'Agregar' : 'Agregar al carrito') : 'Agotado';
      });
      window.history.replaceState({}, '', option.dataset.url);
    });

    let initialVariantMediaId = null;
    const variantData = root.querySelector('[data-product-variants]');
    const optionGroups = [...root.querySelectorAll('[data-option-group]')];
    if (select && variantData && optionGroups.length) {
      const variants = JSON.parse(variantData.textContent);
      const requestedParameters = new URLSearchParams(window.location.search);
      const requestedVariantId = requestedParameters.get('variant');
      const requestedOptionPosition = Number(requestedParameters.get('ptc_option_position')) - 1;
      const requestedOptionValue = requestedParameters.get('ptc_option_value');
      let requestedVariant = variants.find((variant) => String(variant.id) === requestedVariantId);

      if (requestedOptionValue && requestedOptionPosition >= 0) {
        const matchingRequestedVariant = variants.find((variant) => (
          variant.available && variant.options[requestedOptionPosition] === requestedOptionValue
        )) || variants.find((variant) => variant.options[requestedOptionPosition] === requestedOptionValue);
        if (!requestedVariant || requestedVariant.options[requestedOptionPosition] !== requestedOptionValue) {
          requestedVariant = matchingRequestedVariant;
        }
      }

      if (requestedVariant) {
        initialVariantMediaId = requestedVariant.featured_media?.id || requestedVariant.featured_image?.id || null;
        select.value = String(requestedVariant.id);
        optionGroups.forEach((group) => {
          const position = Number(group.dataset.optionPosition);
          const selectedValue = requestedVariant.options[position];
          group.querySelectorAll('[data-option-value]').forEach((button) => {
            const selected = button.dataset.optionValue === selectedValue;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-pressed', String(selected));
          });
          const selectedLabel = group.querySelector('[data-selected-option]');
          if (selectedLabel) selectedLabel.textContent = selectedValue;
        });
      }
      const selectedValues = () => optionGroups.map((group) => group.querySelector('[data-option-value].is-selected')?.dataset.optionValue || '');
      const refreshAvailability = () => {
        const selected = selectedValues();
        optionGroups.forEach((group) => {
          const position = Number(group.dataset.optionPosition);
          const isColorGroup = group.classList.contains('is-color');
          group.querySelectorAll('[data-option-value]').forEach((button) => {
            const available = variants.some((variant) => (
              variant.available
              && variant.options[position] === button.dataset.optionValue
              && (isColorGroup || variant.options.every((value, index) => index === position || value === selected[index]))
            ));
            button.disabled = !available;
            button.classList.toggle('is-unavailable', !available);
            button.setAttribute('aria-label', available ? `Seleccionar ${button.dataset.optionValue}` : `${button.dataset.optionValue} — Agotado`);
          });
        });
      };

      optionGroups.forEach((group) => {
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          button.addEventListener('click', () => {
            if (button.disabled) return;
            const position = Number(group.dataset.optionPosition);
            const isColorGroup = group.classList.contains('is-color');
            const values = selectedValues();
            values[position] = button.dataset.optionValue;
            const exactVariant = variants.find((item) => item.options.every((value, index) => value === values[index]));
            const variant = (exactVariant?.available ? exactVariant : null)
              || (isColorGroup ? variants.find((item) => item.available && item.options[position] === button.dataset.optionValue) : null)
              || exactVariant;
            if (variant) {
              optionGroups.forEach((optionGroup) => {
                const optionPosition = Number(optionGroup.dataset.optionPosition);
                const selectedValue = variant.options[optionPosition];
                optionGroup.querySelectorAll('[data-option-value]').forEach((item) => {
                  const selected = item.dataset.optionValue === selectedValue;
                  item.classList.toggle('is-selected', selected);
                  item.setAttribute('aria-pressed', String(selected));
                });
                const selectedLabel = optionGroup.querySelector('[data-selected-option]');
                if (selectedLabel) selectedLabel.textContent = selectedValue;
              });
              select.value = String(variant.id);
              select.dispatchEvent(new Event('change', { bubbles: true }));
              const mediaId = variant.featured_media?.id || variant.featured_image?.id;
              if (mediaId) root.querySelector(`[data-product-thumbnail][data-target$="-${mediaId}"]`)?.click();
            }
            refreshAvailability();
          });
        });
      });
      refreshAvailability();
      if (requestedVariant && select.value === String(requestedVariant.id)) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    stickyAdd?.addEventListener('click', () => form?.requestSubmit());

    root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = String(Math.max(Number(quantity.min || 1), Number(quantity.value || 1) - 1));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });
    root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = String(Number(quantity.value || 1) + 1);
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const hydrateImage = (image) => {
      if (!image) return Promise.resolve();
      const applySource = () => {
        if (!image.dataset.src) return;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        if (image.dataset.sizes) image.sizes = image.dataset.sizes;
        image.src = image.dataset.src;
        delete image.dataset.src;
        delete image.dataset.srcset;
        delete image.dataset.sizes;
      };
      if (!image.dataset.src && image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => {
          image.removeEventListener('load', done);
          image.removeEventListener('error', done);
          resolve();
        };
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
        applySource();
        if (image.complete) done();
      });
    };

    const productStage = root.querySelector('.ptc-product__stage');
    const galleryThumbnails = [...root.querySelectorAll('[data-product-thumbnail]')];
    const galleryMedia = [...root.querySelectorAll('[data-product-media]')];
    let mediaRequest = 0;

    const preloadMedia = (index) => {
      const media = galleryMedia[index];
      hydrateImage(media?.querySelector('[data-product-deferred-image]'));
    };

    const scrollThumbnailStrip = (thumbnail) => {
      const strip = thumbnail.closest('.ptc-product__thumbnails');
      if (!strip) return;
      const mobile = window.matchMedia('(max-width: 899px)').matches;
      if (mobile) {
        const stripRect = strip.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const centeredLeft = strip.scrollLeft + thumbnailRect.left - stripRect.left - ((stripRect.width - thumbnailRect.width) / 2);
        const maximumLeft = Math.max(0, strip.scrollWidth - strip.clientWidth);
        strip.scrollTo({ left: Math.max(0, Math.min(maximumLeft, centeredLeft)), behavior: 'smooth' });
      } else {
        const stripRect = strip.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const centeredTop = strip.scrollTop + thumbnailRect.top - stripRect.top - ((stripRect.height - thumbnailRect.height) / 2);
        const maximumTop = Math.max(0, strip.scrollHeight - strip.clientHeight);
        strip.scrollTo({ top: Math.max(0, Math.min(maximumTop, centeredTop)), behavior: 'smooth' });
      }
    };

    const activateThumbnail = async (thumbnail) => {
      const target = root.querySelector(`#${CSS.escape(thumbnail.dataset.target)}`);
      if (!target) return;
      const request = ++mediaRequest;
      const targetIndex = galleryMedia.indexOf(target);
      const deferredImage = target.querySelector('[data-product-deferred-image]');

      galleryThumbnails.forEach((item) => {
        const active = item === thumbnail;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      scrollThumbnailStrip(thumbnail);

      if (deferredImage?.dataset.src || (deferredImage && !deferredImage.complete)) {
        productStage?.classList.add('is-loading');
        productStage?.setAttribute('aria-busy', 'true');
        await hydrateImage(deferredImage);
      }
      if (request !== mediaRequest) return;

      galleryMedia.forEach((media) => {
        const active = media === target;
        media.hidden = !active;
        media.classList.toggle('is-active', active);
        if (!active) media.querySelector('video')?.pause();
      });
      productStage?.classList.remove('is-loading');
      productStage?.removeAttribute('aria-busy');
      preloadMedia(targetIndex + 1);
      preloadMedia(targetIndex - 1);
    };

    galleryThumbnails.forEach((thumbnail, index) => {
      thumbnail.tabIndex = thumbnail.classList.contains('is-active') ? 0 : -1;
      thumbnail.addEventListener('click', () => activateThumbnail(thumbnail));
      thumbnail.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = Math.min(galleryThumbnails.length - 1, index + 1);
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = Math.max(0, index - 1);
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = galleryThumbnails.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        galleryThumbnails[nextIndex]?.focus();
        activateThumbnail(galleryThumbnails[nextIndex]);
      });
    });
    if (initialVariantMediaId) {
      root.querySelector(`[data-product-thumbnail][data-target$="-${initialVariantMediaId}"]`)?.click();
    }

    const scheduleIdle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 250));
    scheduleIdle(() => {
      preloadMedia(1);
      preloadMedia(2);
    });

    root.querySelectorAll('[data-thumbnail-carousel]').forEach((carousel) => {
      const thumbnails = [...carousel.querySelectorAll('[data-product-thumbnail]')];
      const strip = carousel.querySelector('.ptc-product__thumbnails');
      const previous = carousel.querySelector('[data-thumbnail-prev]');
      const next = carousel.querySelector('[data-thumbnail-next]');
      const updateControls = () => {
        const activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active')));
        if (previous) previous.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === thumbnails.length - 1;
      };
      const move = (direction) => {
        const activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active')));
        const nextIndex = Math.max(0, Math.min(thumbnails.length - 1, activeIndex + direction));
        thumbnails[nextIndex]?.click();
      };
      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', updateControls));
      updateControls();

      if (strip) strip.addEventListener('scroll', updateControls, { passive: true });
    });

    if (productStage && galleryThumbnails.length > 1) {
      let touchStartX = 0;
      let touchStartY = 0;
      let gallerySwiped = false;

      productStage.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        gallerySwiped = false;
      }, { passive: true });
      productStage.addEventListener('touchend', (event) => {
        if (!window.matchMedia('(max-width: 899px)').matches) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const distance = touch.clientX - touchStartX;
        const verticalDistance = touch.clientY - touchStartY;
        if (Math.abs(distance) < 36 || Math.abs(distance) <= Math.abs(verticalDistance) * 1.1) return;
        const activeIndex = Math.max(0, galleryThumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active')));
        const nextIndex = Math.max(0, Math.min(galleryThumbnails.length - 1, activeIndex + (distance < 0 ? 1 : -1)));
        if (nextIndex !== activeIndex) {
          gallerySwiped = true;
          galleryThumbnails[nextIndex].click();
        }
      }, { passive: true });
      productStage.addEventListener('click', (event) => {
        if (!gallerySwiped) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        gallerySwiped = false;
      }, true);
    }

    const lightbox = root.querySelector('[data-product-lightbox]');
    if (lightbox) {
      const slides = [...lightbox.querySelectorAll('[data-lightbox-slide]')];
      let lightboxIndex = 0;
      let touchStart = 0;
      const showSlide = (index) => {
        lightboxIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === lightboxIndex;
          if (active) hydrateImage(slide.querySelector('[data-lightbox-deferred-image]'));
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });
      };
      root.querySelectorAll('[data-lightbox-open]').forEach((trigger) => trigger.addEventListener('click', () => {
        showSlide(Number(trigger.dataset.lightboxIndex || 0));
        lightbox.showModal();
      }));
      lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => lightbox.close());
      lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => showSlide(lightboxIndex - 1));
      lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => showSlide(lightboxIndex + 1));
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target.matches('.ptc-product-lightbox__window, .ptc-product-lightbox__track, .ptc-product-lightbox__slide')) lightbox.close();
      });
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') showSlide(lightboxIndex - 1);
        if (event.key === 'ArrowRight') showSlide(lightboxIndex + 1);
      });
      lightbox.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0].clientX; }, { passive: true });
      lightbox.addEventListener('touchend', (event) => {
        const distance = event.changedTouches[0].clientX - touchStart;
        if (Math.abs(distance) > 45) showSlide(lightboxIndex + (distance < 0 ? 1 : -1));
      }, { passive: true });
    }

    root.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
      const triggers = [...tabs.querySelectorAll('[data-product-tab]')];
      const panels = [...tabs.querySelectorAll('[data-product-tabpanel]')];
      const activate = (trigger, focus = false) => {
        triggers.forEach((item) => {
          const active = item === trigger;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.id !== trigger.getAttribute('aria-controls'); });
        if (focus) trigger.focus();
      };
      if (triggers.length && !triggers.some((item) => item.getAttribute('aria-selected') === 'true')) activate(triggers[0]);
      else triggers.forEach((trigger) => { trigger.tabIndex = trigger.getAttribute('aria-selected') === 'true' ? 0 : -1; });
      triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => activate(trigger));
        trigger.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % triggers.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + triggers.length) % triggers.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = triggers.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          activate(triggers[nextIndex], true);
        });
      });
    });
};

const initializeProducts = (scope = document) => {
  const roots = [];
  if (scope.matches?.('[data-product-root]')) roots.push(scope);
  roots.push(...scope.querySelectorAll?.('[data-product-root]') || []);
  roots.forEach(initializeProduct);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeProducts());
} else {
  initializeProducts();
}

document.addEventListener('shopify:section:load', (event) => initializeProducts(event.target));
